import { PlayerEvent, PlayerAPI } from 'bitmovin-player';
import user from './user';
import h, { getCountry } from './common';
import apis from './apis';
import { EVENT } from '../contexts/MixpanelConstants';
import { container } from '@intigral/domain/lib/DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { playerTypes } from '../config/constants';
import { ErrorType } from '@intigral/utils';
import type { NetworkErrorInfo } from '@intigral/utils';
import { API_IDS } from '@intigral/constant';
import { IApiPathManager } from '@intigral/domain/lib/infrastructures/config/ApiPathManager';
import playerService from './bitmovinPlayer';
import { isPlayerAlive, safePlayerOff, safePlayerOn } from './playerGuards';

interface Metadata {
    totalDuration: number;
    startedAt: number;
    country: string;
    deviceType: string;
    userInfo: any;
    profileName: string;
    endTime?: number;
    startTime?: number;
    guid: string;
}

interface Config {
    zones: {
        start: { enabled: boolean; percentage: number };
        end: { enabled: boolean; percentage: number };
        stop: { enabled: boolean };
    };
    learningMinimumDelay: number;
}

class LearnManager {
    private player: PlayerAPI;
    private playerType: string;
    private trackError: any;
    private metadata: Metadata;
    private config: Config;
    private startTriggered: boolean;
    private endTriggered: boolean;

    constructor(player: PlayerAPI, playerType: string, asset: any, config: Config, trackError: any) {
        this.player = player;
        this.playerType = playerType;
        this.trackError = trackError;
        this.config = config;
        this.startTriggered = false;
        this.endTriggered = false;
        // Add event listeners to player

        this.onTimeChanged = this.onTimeChanged.bind(this);
    }

    /**
     * Function to handle player destruction.
     */
    onDestroy(): void {
        const player = this.player;
        if (!player) return;

        safePlayerOff(this.player, PlayerEvent.TimeChanged, this.onTimeChanged);

        if (this.config.zones.stop.enabled && this.startTriggered && !this.endTriggered && isPlayerAlive(player)) {
            this.sendStopEvent(this.getPlaybackTimeSeconds(player.getCurrentTime()));
        }

        this.player = null as any;
    }

    init(asset: any): void {
        if (!isPlayerAlive(this.player)) return;

        this.metadata =
            this.playerType === playerTypes.LiveTV
                ? this.getLiveTVmetadata(asset?.video?.selected ?? asset)
                : this.getVODmetadata(asset);

        safePlayerOn(this.player, PlayerEvent.TimeChanged, this.onTimeChanged);
    }

    /**
     * Function to handle time change events.
     * @param event
     *
     * @todo: Should this be throttled to reduce API calls?
     */
    private onTimeChanged(event: any): void {
        if (!isPlayerAlive(this.player)) return;

        // Duration is often 0 if LearnManager init ran before SourceLoaded; refresh once ready.
        let totalDuration = this.metadata.totalDuration;
        if (!totalDuration || totalDuration <= 0 || !Number.isFinite(totalDuration)) {
            try {
                const d = this.player.getDuration();
                if (!d || !Number.isFinite(d) || d <= 0) return;
                this.metadata.totalDuration = d;
                totalDuration = d;
            } catch {
                return;
            }
        }

        const currentTime = Math.max(0, this.getPlaybackTimeSeconds(event.time));
        const timestamp = event.timestamp; // in milliseconds
        const startPercentage = this.config.zones.start.percentage / 100;
        const endPercentage = this.config.zones.end.percentage / 100;
        const startEnabled = this.config.zones.start.enabled;
        const passedMinimumTimeDelay = this.config.learningMinimumDelay * 1000 < timestamp - this.metadata.startedAt;
        const progress = currentTime / totalDuration;
        const passedStartZone = progress >= startPercentage && passedMinimumTimeDelay;
        const passedEndZone = progress >= endPercentage;
        const endEnabled = this.config.zones.end.enabled;

        if (!this.startTriggered && startEnabled && passedStartZone) {
            this.sendStartAction(currentTime);
            this.startTriggered = true;
        }
        if (this.startTriggered && !this.endTriggered && endEnabled && passedEndZone) {
            this.sendEndAction(currentTime);
            this.endTriggered = true;
        }
    }

    private getProgramDurationSeconds(asset: any): number {
        const startTime = Number(asset?.startTime);
        const endTime = Number(asset?.endTime);
        if (!startTime || !endTime || endTime <= startTime) return 0;
        return (endTime - startTime) / 1000;
    }

    /**
     * For live linear, elapsed program time uses wall-clock offset (same as bookmarks).
     * Catchup/rewind uses stream-relative player time.
     */
    private shouldUseProgramElapsedTime(): boolean {
        if (this.playerType !== playerTypes.LiveTV) return false;
        const startTime = this.metadata?.startTime;
        const endTime = this.metadata?.endTime;
        if (!startTime || !endTime) return false;

        try {
            return this.player.isLive();
        } catch {
            return false;
        }
    }

    private getPlaybackTimeSeconds(eventTime?: number): number {
        const time = eventTime ?? this.player.getCurrentTime();

        if (this.shouldUseProgramElapsedTime()) {
            return time - this.metadata.startTime! / 1000;
        }

        return time;
    }

    /**
     * Function to send stop event.
     * @param timeCode
     */
    private sendStopEvent(timeCode: number): void {
        this.sendAction('STOP', timeCode);
    }

    /**
     * Function to send start action.
     * @param timeCode
     */
    private sendStartAction(timeCode: number): void {
        this.sendAction('START', timeCode);
    }

    /**
     * Function to send end action.
     * @param timeCode
     */
    private sendEndAction(timeCode: number): void {
        this.sendAction('END', timeCode);
    }

    /**
     * Function to send action.
     * @param action
     * @param timeCode
     */
    private sendAction(action: string, timeCode: number): void {
        const sessionDuration = Math.floor((new Date().valueOf() - this.metadata.startedAt) / 1000); // in seconds
        const useCaseId = playerService.useCaseId;
        const searchParams = {
            itemGuid: this.metadata.guid,
            userName: this.metadata.userInfo.username ?? user.getUsername(),
            profileName: this.metadata.profileName ?? user.getProfileName(),
            device: this.metadata.deviceType ?? h.getDevice(),
            country: this.metadata.country ?? getCountry(),
            timecode: Math.floor(timeCode),
            sessionDuration: sessionDuration,
            eventType: action,
            sessionStartTs: Math.floor(this.metadata.startedAt / 1000),
            useCaseId,
        };

        const url = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager).getPath(API_IDS.LEARN_EVENTS) ?? '';
        const learnActionService: { learnEvent: (apiKey: string, params: any, token: string) => Promise<any> } =
            container.resolve(DI.SERVICES.ILearnActionService);
        learnActionService
            .learnEvent(user.getAPIKey(), searchParams, user.getIdentityToken())
            .then((res: any) => {
                if (res.error) {
                    const errorInfo: NetworkErrorInfo = {
                        errorType: ErrorType.API_FAILURE,
                        url,
                        statusCode: res?.error?.statusCode,
                        errorData: res?.error,
                    };
                    this.trackError?.(EVENT.RECOMMENDATION.ERROR_API_RECOMMENDATION, errorInfo);
                }
            })
            .catch((errResponse: any) => {
                const errorInfo: NetworkErrorInfo = errResponse?.networkError ?? {
                    errorType: ErrorType.API_FAILURE,
                    url,
                    statusCode: errResponse?.statusCode ?? errResponse?.status,
                    errorData: errResponse?.data ?? errResponse?.error,
                };
                this.trackError?.(EVENT.RECOMMENDATION.ERROR_API_RECOMMENDATION, errorInfo);
            });
    }

    /**
     * Function to get Live TV metadata.
     * @param asset
     * @returns {Metadata}
     */
    private getLiveTVmetadata(asset: any): Metadata {
        const programDuration = this.getProgramDurationSeconds(asset);
        let duration = programDuration;

        if (programDuration <= 0) {
            try {
                const d = this.player.getDuration();
                duration = d === Infinity || d === 0 || !Number.isFinite(d) ? 0 : d;
            } catch (e) {
                void e;
            }
        }

        return {
            totalDuration: duration,
            startedAt: new Date().valueOf(), // milliseconds
            country: getCountry(),
            deviceType: h.getDevice(),
            userInfo: user.getUserInfo(),
            profileName: user.getProfileName(),
            endTime: Number(asset.endTime),
            startTime: Number(asset.startTime),
            guid: asset.listingGuid ?? asset.guid ?? '',
        };
    }

    /**
     * Function to get VOD metadata.
     * @param asset
     * @returns {Metadata}
     */
    private getVODmetadata(asset: any): Metadata {
        let duration = 0;

        try {
            const d = this.player.getDuration();
            duration = d && Number.isFinite(d) && d > 0 ? d : 0;
        } catch (e) {
            void e;
        }

        if (!duration) {
            const assetDuration = Number(asset?.duration ?? asset?.runtime ?? 0);
            duration = assetDuration > 0 ? assetDuration : 0;
        }

        return {
            totalDuration: duration,
            startedAt: new Date().valueOf(), // milliseconds
            country: getCountry(),
            deviceType: h.getDevice(),
            userInfo: user.getUserInfo(),
            profileName: user.getProfileName(),
            guid: asset?.guid,
        };
    }
}

export default LearnManager;
